import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { EMAILS } from "@/lib/emails";

const TITLE = "Security and Vulnerability Disclosure | Shariq Refai, MD";
const DESCRIPTION =
  "How to report a security issue on shariqrefai.com, what's in scope, how reports are handled, and the safe harbor for good faith research. No PHI, please.";
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Security", path: "/security" },
];

export default function SecurityPolicy() {
  const page = webPageSchema({
    path: "/security",
    title: TITLE,
    description: DESCRIPTION,
    breadcrumbs: CRUMBS,
    datePublished: "2026-09-23",
    dateModified: "2026-09-23",
    lastReviewed: "2026-09-23",
  });
  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        url="/security"
        schema={[page, breadcrumbSchema(CRUMBS)]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Security</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Security and Vulnerability Disclosure
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            If you've found a security problem on this site, here's how to
            tell us and what happens next.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: September 23, 2026.</strong>
          </p>

          <p>
            shariqrefai.com is an educational author and media site. It
            doesn't hold patient records or protected health information, and
            it doesn't provide clinical care. We still take its security
            seriously, and we're grateful to anyone who reports a problem
            responsibly.
          </p>

          <h2>How to report</h2>
          <p>
            Email <a href={`mailto:${EMAILS.security}`}>{EMAILS.security}</a>{" "}
            with a short description of the issue, the URL or component
            affected, the steps to reproduce it, and what you think the impact
            is. Screenshots or a proof of concept help. The machine readable
            version of this contact lives at{" "}
            <a href="/.well-known/security.txt">/.well-known/security.txt</a>.
          </p>
          <p>
            Please don't include protected health information, anyone's
            medical details, or personal data you came across while testing.
            For clinical or patient safety concerns, contact{" "}
            <a href="https://shrinkmd.com/" target="_blank" rel="noopener noreferrer">
              shrinkMD
            </a>{" "}
            directly instead.
          </p>

          <h2>What's in scope</h2>
          <ul>
            <li>Pages and files served from shariqrefai.com.</li>
            <li>The contact and notification forms and the functions behind them.</li>
            <li>Security headers, TLS configuration, and anything that could let someone alter what readers see.</li>
          </ul>
          <p>
            Other Shrink Network sites and third-party services such as
            Cloudflare, Google Analytics, and Google Fonts are outside this
            policy. Reports about them should go to their owners.
          </p>

          <h2>What we ask</h2>
          <ul>
            <li>Give us a reasonable window to fix the issue before you share it publicly.</li>
            <li>Don't run denial of service tests, spam the forms, or use automated scanners at a volume that affects readers.</li>
            <li>Don't access, change, or keep data that isn't yours, and stop once you've shown the issue exists.</li>
            <li>Don't try social engineering or physical attacks.</li>
          </ul>

          <h2>What you can expect</h2>
          <p>
            We'll acknowledge your report, keep you posted while we work on
            it, and let you know when it's fixed. If
            you'd like credit, we're happy to name you once the fix is live.
            There's no paid bug bounty.
          </p>

          <h2>Safe harbor</h2>
          <p>
            If you act in good faith and follow this policy, we won't pursue
            legal action against you for your research, and we'll treat your
            work as authorized. If something is unclear, ask first at{" "}
            <a href={`mailto:${EMAILS.security}`}>{EMAILS.security}</a>.
          </p>

          <p>
            Related: <Link href="/privacy">Privacy Policy</Link>,{" "}
            <Link href="/terms">Terms of Use</Link>, and{" "}
            <Link href="/verified">independent verification</Link>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
